import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';
import { fadeInUp, shouldReduceMotion } from '@/lib/animations/optimized-animations';

export interface KickerProps {
  /** The label text. Rendered uppercase. */
  children?: ReactNode;
  /** Optional extra classes merged onto the root element. */
  className?: string;
}

/**
 * Kicker — an uppercase section label preceded by an orange->cyan gradient bar.
 * Sits above headings to introduce a section. Motion is a tiny fade gated on
 * reduced-motion; renders statically when motion is reduced.
 */
export function Kicker({ children, className }: KickerProps): ReactNode {
  const reduce = shouldReduceMotion();

  const content = (
    <>
      <span
        aria-hidden="true"
        className="inline-block h-[3px] w-8 shrink-0 rounded-full"
        style={{ background: 'var(--grad-kicker)' }}
      />
      <span
        className="uppercase font-extrabold leading-none"
        style={{
          color: 'var(--cyan)',
          letterSpacing: '0.10em',
          fontWeight: 800,
        }}
      >
        {children}
      </span>
    </>
  );

  const rootClassName = clsx('flex items-center gap-3', className);

  if (reduce) {
    return <div className={rootClassName}>{content}</div>;
  }

  return (
    <motion.div
      className={rootClassName}
      initial={fadeInUp.initial}
      whileInView={fadeInUp.animate}
      viewport={{ once: true, amount: 0.6 }}
      transition={fadeInUp.transition}
    >
      {content}
    </motion.div>
  );
}

export default Kicker;
